import type { ParentEntity, ParentEntityType } from "@/contexts/DetailContextProvider.types";
import type { ContactTaskRow } from "@/lib/types/entity-rows";

/**
 * Canonical `contact_tasks.entity_type` values (Supabase table names).
 * Also accepts common singular aliases.
 */
export const TASK_LINKED_ENTITY_KINDS = [
  "contacts",
  "deals",
  "companies",
  "tickets",
  "time_entries",
  "offerings",
] as const;

export type TaskLinkedEntityKind = (typeof TASK_LINKED_ENTITY_KINDS)[number];

const ENTITY_TYPE_ALIASES: Record<string, TaskLinkedEntityKind> = {
  contact: "contacts",
  contacts: "contacts",
  deal: "deals",
  deals: "deals",
  company: "companies",
  companies: "companies",
  ticket: "tickets",
  tickets: "tickets",
  time_entry: "time_entries",
  time_entries: "time_entries",
  timeentries: "time_entries",
  offering: "offerings",
  offerings: "offerings",
};

export function normalizeTaskLinkedEntityType(
  raw: string | null | undefined
): TaskLinkedEntityKind | null {
  if (raw == null || typeof raw !== "string") return null;
  const key = raw.trim().toLowerCase();
  return ENTITY_TYPE_ALIASES[key] ?? null;
}

export function getTaskLinkedEntityId(
  item: Pick<ContactTaskRow, "entity_id">
): string | null {
  const id = item.entity_id;
  if (id == null || typeof id !== "string") return null;
  const t = id.trim();
  return t.length > 0 ? t : null;
}

/** True when the task is linked to a communications row via entity_type/entity_id. */
export function isTaskLinkedToCommunications(
  entityType: string | null | undefined
): boolean {
  const key = entityType?.trim().toLowerCase() ?? "";
  return key === "communications" || key === "communication";
}

/**
 * Contact id available synchronously from the task row (contact_id or entity when type is contacts).
 * Does not resolve communications-linked contact — fetch that separately.
 */
export function getTaskDirectContactContextId(
  task: Pick<ContactTaskRow, "contact_id" | "entity_type" | "entity_id">
): string | null {
  const cid = task.contact_id;
  if (cid != null && typeof cid === "string" && cid.trim().length > 0) {
    return cid.trim();
  }
  const kind = normalizeTaskLinkedEntityType(task.entity_type);
  if (kind === "contacts") {
    return getTaskLinkedEntityId(task);
  }
  return null;
}

/** Route to the full Hub page for the linked record (when one exists). */
export function getLinkedEntityFullPageHref(kind: TaskLinkedEntityKind, id: string): string {
  switch (kind) {
    case "contacts":
      return `/contacts/${id}`;
    case "deals":
      return `/deals/${id}`;
    case "companies":
      return `/companies/${id}`;
    case "tickets":
      return `/tickets/${id}`;
    case "time_entries":
      return `/time-tracking/entries/${id}`;
    case "offerings":
      return "/offerings";
    default:
      return "/";
  }
}

function linkedKindToParentType(kind: TaskLinkedEntityKind): ParentEntityType {
  switch (kind) {
    case "contacts":
      return "contact";
    case "companies":
      return "company";
    case "deals":
      return "deal";
    case "tickets":
      return "ticket";
    case "time_entries":
      return "time_entry";
    case "offerings":
      return "offering";
    default:
      return null;
  }
}

/**
 * Parent detail context for nested lists/components while viewing a task's linked-entity tab.
 */
export function taskLinkedEntityToParentEntity(
  task: Pick<ContactTaskRow, "entity_type" | "entity_id" | "title">
): ParentEntity | undefined {
  const kind = normalizeTaskLinkedEntityType(task.entity_type);
  const id = getTaskLinkedEntityId(task);
  if (!kind || !id) return undefined;
  return {
    type: linkedKindToParentType(kind),
    id,
    name: task.title ?? undefined,
  };
}

export function getPrimaryLinkedEntityKind(
  task: Pick<ContactTaskRow, "entity_type" | "entity_id">
): TaskLinkedEntityKind | null {
  const kind = normalizeTaskLinkedEntityType(task.entity_type);
  const id = getTaskLinkedEntityId(task);
  if (!kind || !id) return null;
  return kind;
}

export function linkedEntityTabLabel(kind: TaskLinkedEntityKind): string {
  switch (kind) {
    case "contacts":
      return "Contact";
    case "deals":
      return "Deal";
    case "companies":
      return "Company";
    case "tickets":
      return "Ticket";
    case "time_entries":
      return "Time entry";
    case "offerings":
      return "Offering";
    default:
      return "Linked";
  }
}
